import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { fetchMediaBlobUrl } from "./api";

/**
 * Media is HARD-AUTH (Bearer header). An `<img src>`/`<video src>` cannot send
 * the header, so we fetch the bytes WITH the auth header, wrap them in an
 * object URL, and revoke on unmount (spec §9.3 / task brief). No token
 * query-param hack.
 */

function AuthedImage({ url, alt, onClick }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let alive = true;
    const controller = new AbortController();
    fetchMediaBlobUrl(url, { signal: controller.signal })
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setSrc(u);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  if (failed) {
    return <div className="vdsc-media-fallback">Image unavailable</div>;
  }
  if (!src) {
    return <div className="vdsc-media-loading" aria-hidden="true" />;
  }
  return (
    <img
      className="vdsc-media-img"
      src={src}
      alt={alt || "Attached image"}
      onClick={onClick}
    />
  );
}

function AuthedVideo({ item }) {
  const [posterSrc, setPosterSrc] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  // Poster (also authed-blob).
  useEffect(() => {
    if (!item.poster_url) return undefined;
    let objectUrl = null;
    let alive = true;
    const controller = new AbortController();
    fetchMediaBlobUrl(item.poster_url, { signal: controller.signal })
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setPosterSrc(u);
      })
      .catch(() => {});
    return () => {
      alive = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.poster_url]);

  // Bytes are fetched only when the user hits play (saves bandwidth).
  useEffect(() => {
    if (!playing || videoSrc) return undefined;
    let objectUrl = null;
    let alive = true;
    const controller = new AbortController();
    fetchMediaBlobUrl(item.url, { signal: controller.signal })
      .then((u) => {
        if (!alive) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setVideoSrc(u);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [playing, videoSrc, item.url]);

  if (failed) {
    return <div className="vdsc-media-fallback">Video unavailable</div>;
  }
  if (playing && videoSrc) {
    return (
      <video
        className="vdsc-media-video"
        src={videoSrc}
        controls
        autoPlay
        playsInline
      />
    );
  }
  return (
    <button
      type="button"
      className="vdsc-media-videoposter"
      onClick={() => setPlaying(true)}
      aria-label="Play video"
      style={posterSrc ? { backgroundImage: `url(${posterSrc})` } : undefined}
    >
      <span className="vdsc-media-playbadge">
        <Play size={20} fill="currentColor" />
      </span>
    </button>
  );
}

export default function MediaGrid({ media, onImageClick }) {
  if (!media || media.length === 0) return null;
  const count = media.length;
  const cls = `vdsc-media-grid count-${Math.min(count, 4)}`;
  return (
    <div className={cls}>
      {media.map((m) =>
        m.kind === "VIDEO" ? (
          <div key={m.id} className="vdsc-media-cell">
            <AuthedVideo item={m} />
          </div>
        ) : (
          <div key={m.id} className="vdsc-media-cell">
            <AuthedImage url={m.url} onClick={onImageClick ? () => onImageClick(m) : undefined} />
          </div>
        )
      )}
    </div>
  );
}
